import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

interface VerifyPrefillCtx {
  proofHex: string | null;
  structuredProof: unknown | null;
  setProofHex: (hex: string | null) => void;
  setStructuredProof: (p: unknown | null) => void;
}

const Ctx = createContext<VerifyPrefillCtx | null>(null);

export function VerifyPrefillProvider({ children }: { children: ReactNode }) {
  const [proofHex, setProofHex] = useState<string | null>(null);
  const [structuredProof, setStructuredProof] = useState<unknown | null>(null);

  const value = useMemo<VerifyPrefillCtx>(
    () => ({ proofHex, structuredProof, setProofHex, setStructuredProof }),
    [proofHex, structuredProof],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useVerifyPrefill() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useVerifyPrefill must be used within VerifyPrefillProvider');
  return ctx;
}

export function useOpenProofInVerify() {
  const { setStructuredProof, setProofHex } = useVerifyPrefill();
  const navigate = useNavigate();
  const openStructured = useCallback(
    (proof: unknown) => {
      setStructuredProof(proof);
      navigate('/verify/viewer');
    },
    [setStructuredProof, navigate],
  );
  const openHex = useCallback(
    (hex: string) => {
      setProofHex(hex);
      navigate('/verify/inspector');
    },
    [setProofHex, navigate],
  );
  return useMemo(() => ({ openStructured, openHex }), [openStructured, openHex]);
}
